import { supabase } from '../lib/supabaseClient';

const STORAGE_KEY = 'refuge_employee';

// The PIN is the credential the admin write RPCs verify server-side, so it is
// held in memory only -- never in localStorage alongside the employee record.
// A page refresh clears it, and callers re-prompt via getSessionPin() === null.
let sessionPin: string | null = null;

export interface Employee {
  id: string;
  name: string;
  email?: string;
  is_admin: boolean;
  photo_url?: string;
}

/**
 * Login with PIN - returns employee if valid
 */
export async function loginWithPIN(pin: string): Promise<{ data: Employee | null; error: any }> {
  try {
    const { data, error } = await supabase
      .rpc('get_employee_by_pin', { p_pin: pin });

    if (error) {
      console.error('[PIN Auth] RPC error:', error);
      return { data: null, error };
    }

    if (!data || data.length === 0) {
      return { data: null, error: { message: 'Invalid PIN' } };
    }

    const employee = data[0] as Employee;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(employee));
    sessionPin = pin;

    // Stamp last login time (fire-and-forget)
    supabase
      .from('employees')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', employee.id);

    console.log('[PIN Auth] Login success:', employee.name);
    return { data: employee, error: null };
  } catch (err) {
    console.error('[PIN Auth] Unexpected error:', err);
    return { data: null, error: err };
  }
}

/**
 * Get current employee from localStorage
 */
export function getCurrentEmployee(): Employee | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Check if current user is admin
 */
export function isAdmin(): boolean {
  const employee = getCurrentEmployee();
  return employee?.is_admin ?? false;
}

/**
 * PIN for the current page session, or null if it needs re-prompting
 */
export function getSessionPin(): string | null {
  return sessionPin;
}

/**
 * Cache a PIN for this page session after it has been verified
 */
export function setSessionPin(pin: string): void {
  sessionPin = pin;
}

/**
 * Verify a PIN belongs to an admin, without disturbing the current session
 */
export async function verifyAdminPIN(pin: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('verify_admin_pin', { p_pin: pin });

  if (error) {
    console.error('[PIN Auth] Verify error:', error);
    return false;
  }

  return data === true;
}

/**
 * Logout - clear localStorage
 */
export function logout(): void {
  localStorage.removeItem(STORAGE_KEY);
  sessionPin = null;
  console.log('[PIN Auth] Logged out');
}

// Alias for clarity
export { logout as logoutWithPIN };

/**
 * Check if employee is logged in
 */
export function isAuthenticated(): boolean {
  return getCurrentEmployee() !== null;
}
